import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkJiraStatus } from '../../../api/integrations';

const JiraAuthSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [connectionDetails, setConnectionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(5);

  // Extract query parameters from URL
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status');
  const message = queryParams.get('message');

  useEffect(() => {
    // Set up countdown timer for redirect
    const countdownInterval = setInterval(() => {
      setCountdown(prevCount => {
        if (prevCount <= 1) {
          clearInterval(countdownInterval);
          
          // Redirect with status parameter to indicate successful auth
          const returnToUrl = localStorage.getItem('jira_return_to') || '/me/integrations?status=success&from_auth=jira';
          localStorage.removeItem('jira_return_to'); // Clean up
          
          // Navigate to the return URL
          window.location.href = returnToUrl;
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);

    // Check Jira connection status
    const checkConnection = async () => {
      try {
        const response = await checkJiraStatus();
        setConnectionDetails(response);
        setLoading(false);
      } catch (error) {
        console.error('Failed to check Jira status:', error);
        setError('Could not verify the connection status');
        setLoading(false);
      }
    };

    checkConnection();
    
    // Clear timer on unmount
    return () => clearInterval(countdownInterval);
  }, [navigate]);

  // Handle if there's an error status in the URL
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md">
          <div className="text-center">
            <svg 
              className="h-16 w-16 text-red-500 mx-auto mb-4" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Connection Failed</h2>
            <p className="text-lg text-gray-600 mb-6">{message || 'There was an error connecting to Jira.'}</p>
            <div className="flex flex-col items-center">
              <button
                onClick={() => navigate('/me/integrations')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Return to Integrations
              </button>
              <p className="text-sm text-gray-500 mt-4">
                Redirecting in {countdown} seconds...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          {loading ? (
            <>
              <svg 
                className="animate-spin h-16 w-16 text-blue-500 mx-auto mb-4" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Checking Connection</h2>
              <p className="text-lg text-gray-600">Verifying your Jira connection...</p>
              <div className="mt-8 inline-block w-16 h-16 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-blue-600 font-medium">{countdown}</span>
                </div>
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle 
                    className="text-gray-200" 
                    strokeWidth="8" 
                    stroke="currentColor" 
                    fill="transparent" 
                    r="45" 
                    cx="50" 
                    cy="50" 
                  />
                  <circle 
                    className="text-blue-600" 
                    strokeWidth="8" 
                    strokeDasharray={282.7433388230814} 
                    strokeDashoffset={282.7433388230814 - (282.7433388230814 * countdown / 5)} 
                    strokeLinecap="round" 
                    stroke="currentColor" 
                    fill="transparent" 
                    r="45" 
                    cx="50" 
                    cy="50" 
                  />
                </svg>
              </div>
            </>
          ) : error ? (
            <>
              <svg 
                className="h-16 w-16 text-yellow-500 mx-auto mb-4" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Connection Status Unknown</h2>
              <p className="text-lg text-gray-600 mb-6">{error}</p>
              <p className="text-sm text-gray-500 mb-4">Redirecting to integrations in {countdown} seconds...</p>
              <div className="inline-block w-16 h-16 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-yellow-500 font-medium">{countdown}</span>
                </div>
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle 
                    className="text-gray-200" 
                    strokeWidth="8" 
                    stroke="currentColor" 
                    fill="transparent" 
                    r="45" 
                    cx="50" 
                    cy="50" 
                  />
                  <circle 
                    className="text-yellow-500" 
                    strokeWidth="8" 
                    strokeDasharray={282.7433388230814} 
                    strokeDashoffset={282.7433388230814 - (282.7433388230814 * countdown / 5)} 
                    strokeLinecap="round" 
                    stroke="currentColor" 
                    fill="transparent" 
                    r="45" 
                    cx="50" 
                    cy="50" 
                  />
                </svg>
              </div>
            </>
          ) : connectionDetails?.is_connected ? (
            <>
              <svg 
                className="h-16 w-16 text-green-500 mx-auto mb-4" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Successfully Connected!</h2>
              <p className="text-lg text-gray-600">Your Jira account has been successfully connected to Darknore.</p>
              
              {connectionDetails.jira_instance_url && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg text-left">
                  <h3 className="font-medium text-gray-700 mb-2">Connection Details</h3>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Instance URL:</span> {connectionDetails.jira_instance_url}
                  </p>
                </div>
              )}
              
              <div className="mt-8">
                <p className="text-sm text-gray-500 mb-2">Redirecting to integrations in {countdown} seconds...</p>
                <div className="inline-block w-16 h-16 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-green-500 font-medium">{countdown}</span>
                  </div>
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle 
                      className="text-gray-200" 
                      strokeWidth="8" 
                      stroke="currentColor" 
                      fill="transparent" 
                      r="45" 
                      cx="50" 
                      cy="50" 
                    />
                    <circle 
                      className="text-green-500" 
                      strokeWidth="8" 
                      strokeDasharray={282.7433388230814} 
                      strokeDashoffset={282.7433388230814 - (282.7433388230814 * countdown / 5)} 
                      strokeLinecap="round" 
                      stroke="currentColor" 
                      fill="transparent" 
                      r="45" 
                      cx="50" 
                      cy="50" 
                    />
                  </svg>
                </div>
              </div>
            </>
          ) : (
            <>
              <svg 
                className="h-16 w-16 text-yellow-500 mx-auto mb-4" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Connection Status Unknown</h2>
              <p className="text-lg text-gray-600 mb-6">Unable to verify the connection status.</p>
              <p className="text-sm text-gray-500 mb-4">Redirecting to integrations in {countdown} seconds...</p>
              <div className="inline-block w-16 h-16 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-yellow-500 font-medium">{countdown}</span>
                </div>
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle 
                    className="text-gray-200" 
                    strokeWidth="8" 
                    stroke="currentColor" 
                    fill="transparent" 
                    r="45" 
                    cx="50" 
                    cy="50" 
                  />
                  <circle 
                    className="text-yellow-500" 
                    strokeWidth="8" 
                    strokeDasharray={282.7433388230814} 
                    strokeDashoffset={282.7433388230814 - (282.7433388230814 * countdown / 5)} 
                    strokeLinecap="round" 
                    stroke="currentColor" 
                    fill="transparent" 
                    r="45" 
                    cx="50" 
                    cy="50" 
                  />
                </svg>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JiraAuthSuccess; 

